import type { $ } from "../../../dom";
import {
  SimpleCheckboxInput,
  type SimpleCheckboxInputOptions,
} from "../simple";

export type WebflowCheckboxInputOptions = SimpleCheckboxInputOptions;

/** Class Webflow puts on the styled box to draw a custom checkbox as checked. */
const CHECKED_CLASS = "w--redirected-checked";

/**
 * A Webflow checkbox field.
 *
 * Reads and writes the real `input[type="checkbox"]`, like
 * {@link SimpleCheckboxInput}, and additionally keeps Webflow's own visual
 * state in step.
 *
 * It used to point `$input` at `.w-checkbox-input` instead. On a custom Webflow
 * checkbox that class sits on the styled `<div>`, not on the input — and since
 * checked state is read and written through `HTMLInputElement`, every option
 * reported itself as unchecked and could not be set either. The real input is
 * present in both the default and custom markup, just visually hidden in the
 * latter.
 */
export class WebflowCheckboxInput extends SimpleCheckboxInput {
  public readonly $label: $;
  /**
   * The styled box Webflow draws in place of the native control.
   *
   * On a default checkbox this *is* the input, so the class written below lands
   * on an element that does not use it — harmless, and it keeps the two markups
   * on one code path.
   */
  public readonly $box: $;

  constructor($root: $, options: WebflowCheckboxInputOptions = {}) {
    super($root, options);

    this.$label = this.$root.find(".w-form-label");
    this.$box = this.$root.find(".w-checkbox-input");
  }

  get isActive(): boolean {
    return super.isActive;
  }

  /**
   * Writes the input, then Webflow's visual state.
   *
   * A custom checkbox hides the input and draws its checked state entirely
   * from this class, so anything that unchecks the input on its own — a reset,
   * `toggleAll`, the `maxActiveCount` cap dropping the oldest selection — left
   * the box looking checked while the filter no longer counted it.
   *
   * Only overridden here: the generic control knows nothing of Webflow's
   * chrome, which stays in this wrapper alongside `$label`.
   */
  set isActive(value: boolean) {
    super.isActive = value;

    this.$box.class(CHECKED_CLASS, value);
  }
}
